import { Icon, Text } from '@vinted/web-ui'
import { ExclamationPoint24, Flag24, LightBulb24 } from '@vinted/monochrome-icons'

type Conversation = {
  messages: Array<string>
  avatarImageSrc: string
}

type BulletPoint = {
  text: string
  icon: React.ComponentProps<typeof Icon>['name']
}

type Slide = {
  title: string
  body: string
  image: string
  sectionTitle?: string
  step1?: string
  step2?: string
  step3?: string
  helpPrompt?: string
  cta?: string
  conversations?: Array<Conversation>
  bulletPoints?: Array<BulletPoint>
}

type ABTestConfig = {
  introSlide: Slide
  carouselSlides: Array<Slide>
  buttonTranslations: {
    intro: string
    close: string
  }
  showConversationLabels: boolean
  titleType: React.ComponentProps<typeof Text>['type']
  showIntroSpacer: boolean
  showDividerOnEducationOnly: boolean
}

export const MANDATORY_INTRO_SLIDE_TIME = 2000

export const AB_TEST_VARIANTS: Record<'old' | 'new', ABTestConfig> = {
  old: {
    introSlide: {
      title: 'intro_slide.title',
      body: 'intro_slide.body',
      image: 'letter-vinted-app.svg',
    },
    carouselSlides: [
      {
        title: 'personal_info_slide.title',
        body: 'personal_info_slide.body',
        image: 'phone-lock.svg',
        conversations: [
          {
            messages: ['personal_info_slide.messages.message_1'],
            avatarImageSrc: 'avatar-1.png',
          },
          {
            messages: ['personal_info_slide.messages.message_2'],
            avatarImageSrc: 'avatar-2.png',
          },
          {
            messages: ['personal_info_slide.messages.message_3'],
            avatarImageSrc: 'avatar-3.png',
          },
        ],
      },
      {
        title: 'avoid_links_slide.title',
        body: 'avoid_links_slide.body',
        image: 'phone-hands.svg',
        conversations: [
          {
            messages: ['avoid_links_slide.messages.message_1'],
            avatarImageSrc: 'avatar-4.png',
          },
          {
            messages: ['avoid_links_slide.messages.message_2', 'qr-code.png'],
            avatarImageSrc: 'avatar-5.png',
          },
          {
            messages: ['avoid_links_slide.messages.message_3'],
            avatarImageSrc: 'avatar-6.png',
          },
        ],
      },
      {
        title: 'report_messages_slide.title',
        body: 'report_messages_slide.body',
        image: 'megaphone-messages.svg',
      },
    ],
    buttonTranslations: {
      intro: 'actions.see_tips',
      close: 'actions.close',
    },
    showConversationLabels: true,
    titleType: Text.Type.Heading,
    showIntroSpacer: false,
    showDividerOnEducationOnly: true,
  },
  new: {
    introSlide: {
      title: 'intro_screen.header',
      body: 'intro_screen.body',
      image: 'letter-vinted-app.svg',
      bulletPoints: [
        {
          text: 'intro_screen.bullet_1',
          icon: ExclamationPoint24,
        },
        {
          text: 'intro_screen.bullet_2',
          icon: LightBulb24,
        },
        {
          text: 'intro_screen.bullet_3',
          icon: Flag24,
        },
      ],
    },
    carouselSlides: [
      {
        title: 'education_screen_1.header',
        body: 'education_screen_1.body',
        image: 'phone-lock.svg',
        sectionTitle: 'education_screen_1.section_title',
        conversations: [
          {
            messages: ['education_screen_1.example_1_new'],
            avatarImageSrc: 'avatar-1.png',
          },
          {
            messages: ['education_screen_1.example_2'],
            avatarImageSrc: 'avatar-2.png',
          },
          {
            messages: ['education_screen_1.example_3'],
            avatarImageSrc: 'avatar-3.png',
          },
          {
            messages: ['education_screen_1.example_4'],
            avatarImageSrc: 'avatar-4.png',
          },
        ],
      },
      {
        title: 'education_screen_2.header',
        body: 'education_screen_2.body',
        image: 'phone-hands.svg',
        sectionTitle: 'education_screen_1.section_title',
        conversations: [
          {
            messages: ['education_screen_2.example_1'],
            avatarImageSrc: 'avatar-4.png',
          },
          {
            messages: ['education_screen_2.example_2', 'qr-code.png'],
            avatarImageSrc: 'avatar-5.png',
          },
          {
            messages: ['education_screen_2.example_3_new'],
            avatarImageSrc: 'avatar-6.png',
          },
        ],
      },
      {
        title: 'education_screen_3.header',
        body: 'education_screen_3.body',
        image: 'megaphone-messages.svg',
        step1: 'education_screen_3.step_1',
        step2: 'education_screen_3.step_2',
        step3: 'education_screen_3.step_3',
        sectionTitle: 'education_screen_3.section_title',
        helpPrompt: 'education_screen_3.help_prompt_new',
        cta: 'education_screen_3.cta',
      },
    ],
    buttonTranslations: {
      intro: 'intro_screen.cta',
      close: 'education_screen_3.cta',
    },
    showConversationLabels: false,
    titleType: Text.Type.HeadingXL,
    showIntroSpacer: true,
    showDividerOnEducationOnly: true,
  },
} as const

export type { Conversation, BulletPoint, Slide, ABTestConfig }
